import React, { useEffect, useRef } from 'react';

/**
 * Custom hook for handling swipe-to-close gesture on mobile menu
 * Closes menu when user swipes down from the top
 */
export const useSwipeGesture = (
  isOpen: boolean,
  onClose: () => void,
  menuRef: React.RefObject<HTMLElement>
) => {
  const startTouchY = useRef(0);

  useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches[0]) {
        startTouchY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!e.touches[0]) return;
      const currentY = e.touches[0].clientY;
      const diff = currentY - startTouchY.current;

      // Prevent default scroll behavior when swiping down at the top
      if (diff > 0 && menuRef.current?.scrollTop === 0) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!e.changedTouches[0]) return;
      const endY = e.changedTouches[0].clientY;
      const diff = endY - startTouchY.current;

      // If swiped down more than 150px and at the top, close menu
      if (diff > 150 && menuRef.current?.scrollTop === 0) {
        onClose();
      }
    };

    const menuElement = menuRef.current;
    menuElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    menuElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    menuElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      menuElement.removeEventListener('touchstart', handleTouchStart);
      menuElement.removeEventListener('touchmove', handleTouchMove);
      menuElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, onClose, menuRef]);
};
